//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import {
  AddCreatorNoteRequest,
  CreatorNotesBasicRequest,
  CreatorNotesRequest,
  CreatorNotesResponse,
  EditCreatorNoteRequest,
} from '@apis/creator-notes/CreatorNotesApiModel';
import { deserializeCreatorNotes } from '@apis/creator-notes/CreatorNotesApiUtils';
import { makeParams } from '@utils/makeParams';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

const CREATOR_NOTES_DEFAULT = '/api/v1/notes';

const creatorNotesFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'affable_gateway',
    `${CREATOR_NOTES_DEFAULT}${endpoint}`,
    init,
  );
};

/**
 * GET /api/v1/notes/creators/:username?network=
 *
 * Get Creators Notes by username and network
 */
export const getCreatorNotes = async (
  req: CreatorNotesRequest,
  init?: RequestInit,
): Promise<CreatorNotesResponse[]> => {
  const { username, network } = req;
  const params = makeParams({ network });
  const res = await creatorNotesFetch(`/creator/${username}?${params}`, {
    ...init,
    method: 'GET',
  });
  const creatorNotesResponse = await res.json<{
    data: CreatorNotesResponse[];
  }>((res) => ({
    data: res.data.map((data) => deserializeCreatorNotes(data)),
  }));

  return creatorNotesResponse.data;
};

/**
 * POST /api/v1/notes/creators/:username?network=
 *
 * Add creator notes by username and network
 */
export const addCreatorNotes = async (
  req: AddCreatorNoteRequest,
  init?: RequestInit,
): Promise<void> => {
  const { username, network, note } = req;
  const params = makeParams({ network });
  await creatorNotesFetch(`/creator/${username}?${params}`, {
    ...init,
    method: 'POST',
    body: JSON.stringify({ note }),
  });
};

/**
 * DELETE /api/v1/notes/creator/:noteId
 *
 * Delete note added to a creator by noteId
 * @param req.noteId - The unique identifier for the note
 */
export const deleteCreatorNote = async (
  req: CreatorNotesBasicRequest,
  init?: RequestInit,
): Promise<void> => {
  const { noteId } = req;
  await creatorNotesFetch(`/creator/${noteId}`, {
    ...init,
    method: 'DELETE',
  });
};

/**
 * PATCH /api/v1/notes/creator/:noteId
 *
 * Update note added to a creator by noteId
 * @param req.noteId - The unique identifier for the note
 * @param req.note - The updated note
 */
export const editCreatorNote = async (
  req: EditCreatorNoteRequest,
  init?: RequestInit,
): Promise<void> => {
  const { noteId, note } = req;
  await creatorNotesFetch(`/creator/${noteId}`, {
    ...init,
    method: 'PATCH',
    body: JSON.stringify({ note: req.note }),
  });
};
